import CriticalityBadge from './CriticalityBadge';
import CountUpAnimation from './UI/CountUpAnimation';
import { useTranslation } from 'react-i18next';

export default function AwarenessCard({ percentage = 90, testo = "", size = 180, strokeWidth = 16 }) {
  const { t } = useTranslation();
  
  const mostraTesto = typeof testo === "string" && testo.trim() !== "";

  // Calcoli per il cerchio SVG (come CircularProgressCard)
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percentage / 100) * circumference;

  // Determina colore gradiente basato su percentuale
  const getGradientColors = () => {
    if (percentage >= 80) return { start: '#10b981', end: '#3b82f6' };
    if (percentage >= 60) return { start: '#f59e0b', end: '#eab308' };
    return { start: '#ef4444', end: '#dc2626' };
  };

  const getTextColor = () => {
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const gradientColors = getGradientColors();

  // Componente cerchio riutilizzabile (stesso stile di CircularProgressCard)
  const CircleProgress = ({ circleSize = size }) => {
    const circleRadius = (circleSize - strokeWidth) / 2;
    const circleCircumference = 2 * Math.PI * circleRadius;
    const circleOffset = circleCircumference - (percentage / 100) * circleCircumference;
    
    return (
      <div className="relative">
        <svg width={circleSize} height={circleSize} className="block">
          <defs>
            {/* Gradiente lineare giallo (da giallo chiaro a giallo più scuro) - come Dashboard */}
            <linearGradient id="awarenessGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#FFE35F" />
              <stop offset="100%" stopColor="#FCB500" />
            </linearGradient>
            <filter id="awarenessInnerShadow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur in="SourceAlpha" stdDeviation="4" result="blur" />
              <feOffset dx="0" dy="2" />
              <feComposite in2="SourceAlpha" operator="arithmetic" k2="-1" k3="1" result="shadowDiff" />
              <feFlood floodColor="#000000" floodOpacity="0.3" />
              <feComposite in2="shadowDiff" operator="in" />
              <feComposite in2="SourceGraphic" operator="over" />
            </filter>
          </defs>

          {/* Cerchio di base (trail) */}
          <circle
            stroke="#e5e7eb"
            fill="#F3F4F7"
            strokeWidth={strokeWidth}
            cx={circleSize / 2}
            cy={circleSize / 2}
            r={circleRadius}
            filter="url(#awarenessInnerShadow)"
          />

          {/* Cerchio progress con gradiente */}
          <circle
            stroke="url(#awarenessGradient)"
            fill="none"
            strokeWidth={strokeWidth}
            strokeDasharray={circleCircumference}
            strokeDashoffset={circleOffset}
            strokeLinecap="round"
            cx={circleSize / 2}
            cy={circleSize / 2}
            r={circleRadius}
            className="transition-all duration-500"
            style={{ transform: 'rotate(-90deg)', transformOrigin: 'center' }}
          />
        </svg>

        {/* Testo al centro */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-4xl font-bold text-gray-800">
            <CountUpAnimation end={percentage} />
          </div>
          <div className="text-lg font-semibold text-gray-400">%</div>
          <p className="text-[10px] font-semibold text-gray-500 mt-1 uppercase tracking-wide">
            {t('awareness.title')}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="font-inter bg-gradient-to-br from-white via-blue-50/20 to-purple-50/10 border-2 border-gray-100 rounded-xl shadow-md p-5 h-full w-full flex items-center justify-center hover:shadow-lg transition-all">
      {mostraTesto ? (
        <div className="grid grid-cols-2 gap-6 w-full items-center">
          {/* Testo a sinistra */}
          <div className="text-gray-700 leading-relaxed text-sm bg-white/50 backdrop-blur-sm rounded-lg p-4 shadow-sm">
            {testo}
          </div>

          {/* Colonna destra - Score e Badge */}
          <div className="flex flex-col items-center justify-center gap-5">
            <CircleProgress circleSize={112} />
            
            {/* Badge criticità */}
            <div className="flex flex-col items-center gap-3">
              <CriticalityBadge punteggio={percentage} />
              <p className={`text-sm font-bold uppercase tracking-widest ${getTextColor()}`}>
                {t('awareness.criticalityLevel')}
              </p>
            </div>
          </div>
        </div>
      ) : (
        // Layout orizzontale: cerchio a sinistra, badge a destra
        <div className="flex flex-row items-center justify-center gap-16 w-full py-4">
          {/* Score circolare a sinistra */}
          <div className="flex justify-center flex-shrink-0">
            <CircleProgress circleSize={176} />
          </div>
          
          {/* Badge e label a destra */}
          <div className="flex flex-col items-center justify-center gap-3 flex-shrink-0">
            <CriticalityBadge punteggio={percentage} />
            <p className={`text-sm font-bold uppercase tracking-widest ${getTextColor()}`}>
              {t('awareness.criticalityLevel')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
